import React, { useEffect, useState } from 'react';
import { Sun, Moon } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * LoginPage — page de connexion Premium Excellence (Session D, P1).
 * Light élégant par défaut, dark possible mais pas par défaut.
 * Logo hexagone, glow bleu nuit, focus violet, footer signature.
 */
export default function LoginPage({ loginData, setLoginData, onSubmit, isLoggingIn }) {
    const { isDark, toggleTheme } = useTheme();
    const [focusedField, setFocusedField] = useState(null);
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => setMounted(true), 30);
        return () => clearTimeout(t);
    }, []);

    const ACCENT_VIOLET = '#8b5cf6';
    const ACCENT_BLUE = '#1D4ED8';
    const ACCENT_BLUE_HOVER = '#1e40af';
    const ACCENT_BLUE_VIVID = '#3b82f6';

    const bgLayer = isDark
        ? 'radial-gradient(ellipse at top, rgba(29,78,216,0.10) 0%, transparent 55%), radial-gradient(ellipse at bottom right, rgba(139,92,246,0.06) 0%, transparent 50%), var(--bg)'
        : 'radial-gradient(ellipse at top, rgba(29,78,216,0.06) 0%, transparent 55%), radial-gradient(ellipse at bottom right, rgba(139,92,246,0.045) 0%, transparent 50%), linear-gradient(180deg, #fafbff 0%, #f4f6fc 100%)';

    const cardShadow = isDark
        ? '0 24px 60px rgba(0,0,0,0.55), 0 0 0 1px rgba(139,92,246,0.10), 0 8px 24px rgba(29,78,216,0.18)'
        : '0 24px 64px rgba(29,78,216,0.10), 0 0 0 1px rgba(29,78,216,0.04), 0 4px 16px rgba(29,78,216,0.06)';

    const inputBorder = (field) =>
        focusedField === field
            ? `1.5px solid ${ACCENT_VIOLET}`
            : `1px solid var(--input-border)`;

    const inputShadow = (field) =>
        focusedField === field
            ? `0 0 0 3px rgba(139,92,246,0.14)`
            : 'none';

    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                background: bgLayer,
                padding: '24px',
                overflow: 'auto',
            }}
        >
            <style>{`
                @keyframes loginFadeIn {
                    from { opacity: 0; transform: translateY(8px) scale(0.985); }
                    to { opacity: 1; transform: translateY(0) scale(1); }
                }
                @keyframes loginLogoGlow {
                    0%, 100% { filter: drop-shadow(0 4px 18px rgba(29,78,216,0.18)); }
                    50% { filter: drop-shadow(0 6px 24px rgba(139,92,246,0.22)); }
                }
                .itriia-login-input::placeholder {
                    color: var(--text-muted);
                    opacity: 0.65;
                }
                .itriia-login-button {
                    transition: transform .15s ease, box-shadow .25s ease, filter .15s ease;
                }
                .itriia-login-button:not(:disabled):hover {
                    transform: translateY(-1px);
                    box-shadow: 0 12px 28px rgba(29,78,216,0.32), 0 0 18px rgba(59,130,246,0.28);
                    filter: brightness(1.05);
                }
                .itriia-login-button:not(:disabled):active {
                    transform: translateY(0);
                }
            `}</style>

            {/* Toggle theme discret haut-droite */}
            <div
                onClick={toggleTheme}
                title={isDark ? 'Passer en mode Jour' : 'Passer en mode Nuit'}
                style={{
                    position: 'absolute',
                    top: 24,
                    right: 28,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    cursor: 'pointer',
                    userSelect: 'none',
                    padding: '6px 10px',
                    borderRadius: 999,
                    background: isDark ? 'rgba(26,26,40,0.55)' : 'rgba(255,255,255,0.70)',
                    backdropFilter: 'blur(6px)',
                    WebkitBackdropFilter: 'blur(6px)',
                    border: `1px solid ${isDark ? 'rgba(139,92,246,0.15)' : 'rgba(29,78,216,0.10)'}`,
                    opacity: 0.85,
                }}
            >
                <Sun size={13} style={{ color: 'var(--toggle-icon-sun)' }} />
                <div className="toggle-track">
                    <div className="toggle-knob" />
                </div>
                <Moon size={13} style={{ color: 'var(--toggle-icon-moon)' }} />
            </div>

            {/* Card principale */}
            <div
                style={{
                    width: 440,
                    maxWidth: '92vw',
                    background: 'var(--card-bg)',
                    border: `1px solid var(--card-border)`,
                    borderRadius: 18,
                    padding: '44px 40px 32px',
                    boxShadow: cardShadow,
                    animation: mounted ? 'loginFadeIn 0.55s cubic-bezier(0.16, 1, 0.3, 1) both' : 'none',
                    position: 'relative',
                }}
            >
                {/* Logo hexagone */}
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 22 }}>
                    <img
                        src={isDark ? '/logo-itriia-white.png' : '/logo-itriia-dark.png'}
                        alt="ITRIIA"
                        style={{
                            width: 78,
                            height: 78,
                            objectFit: 'contain',
                            animation: 'loginLogoGlow 5s ease-in-out infinite',
                        }}
                    />
                </div>

                {/* Titre + tagline éditorial */}
                <div style={{ textAlign: 'center', marginBottom: 30 }}>
                    <h1
                        style={{
                            fontSize: 26,
                            fontWeight: 600,
                            letterSpacing: '-0.01em',
                            color: 'var(--text)',
                            margin: 0,
                            marginBottom: 8,
                        }}
                    >
                        Plateforme ITRIIA
                    </h1>
                    <p
                        style={{
                            fontSize: 13.5,
                            color: 'var(--text-secondary)',
                            margin: 0,
                            fontStyle: 'italic',
                            letterSpacing: '0.005em',
                        }}
                    >
                        L'écosystème de la connaissance vivante
                    </p>
                </div>

                <form onSubmit={onSubmit}>
                    <div style={{ marginBottom: 14 }}>
                        <label
                            htmlFor="itriia-login-username"
                            style={{
                                display: 'block',
                                fontSize: 11.5,
                                fontWeight: 600,
                                letterSpacing: '0.04em',
                                textTransform: 'uppercase',
                                color: 'var(--text-secondary)',
                                marginBottom: 6,
                            }}
                        >
                            Identifiant
                        </label>
                        <input
                            id="itriia-login-username"
                            className="itriia-login-input"
                            type="text"
                            placeholder="votre nom d'utilisateur"
                            value={loginData.username}
                            onChange={(e) => setLoginData((prev) => ({ ...prev, username: e.target.value }))}
                            onFocus={() => setFocusedField('username')}
                            onBlur={() => setFocusedField(null)}
                            autoFocus
                            required
                            style={{
                                width: '100%',
                                boxSizing: 'border-box',
                                padding: '12px 14px',
                                border: inputBorder('username'),
                                borderRadius: 10,
                                fontSize: 14,
                                color: 'var(--text)',
                                backgroundColor: 'var(--input-bg)',
                                boxShadow: inputShadow('username'),
                                outline: 'none',
                                transition: 'border .15s ease, box-shadow .2s ease',
                            }}
                        />
                    </div>

                    <div style={{ marginBottom: 26 }}>
                        <label
                            htmlFor="itriia-login-password"
                            style={{
                                display: 'block',
                                fontSize: 11.5,
                                fontWeight: 600,
                                letterSpacing: '0.04em',
                                textTransform: 'uppercase',
                                color: 'var(--text-secondary)',
                                marginBottom: 6,
                            }}
                        >
                            Mot de passe
                        </label>
                        <input
                            id="itriia-login-password"
                            className="itriia-login-input"
                            type="password"
                            placeholder="••••••••"
                            value={loginData.password}
                            onChange={(e) => setLoginData((prev) => ({ ...prev, password: e.target.value }))}
                            onFocus={() => setFocusedField('password')}
                            onBlur={() => setFocusedField(null)}
                            required
                            style={{
                                width: '100%',
                                boxSizing: 'border-box',
                                padding: '12px 14px',
                                border: inputBorder('password'),
                                borderRadius: 10,
                                fontSize: 14,
                                color: 'var(--text)',
                                backgroundColor: 'var(--input-bg)',
                                boxShadow: inputShadow('password'),
                                outline: 'none',
                                transition: 'border .15s ease, box-shadow .2s ease',
                            }}
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={isLoggingIn}
                        className="itriia-login-button"
                        style={{
                            width: '100%',
                            background: `linear-gradient(135deg, ${ACCENT_BLUE} 0%, ${ACCENT_BLUE_VIVID} 100%)`,
                            color: 'white',
                            border: 'none',
                            padding: '13px',
                            borderRadius: 10,
                            fontSize: 14,
                            fontWeight: 600,
                            letterSpacing: '0.01em',
                            cursor: isLoggingIn ? 'wait' : 'pointer',
                            opacity: isLoggingIn ? 0.75 : 1,
                            boxShadow: '0 6px 18px rgba(29,78,216,0.22)',
                        }}
                    >
                        {isLoggingIn ? 'Connexion…' : 'Se connecter'}
                    </button>
                </form>
            </div>

            {/* Footer signature */}
            <div
                style={{
                    position: 'absolute',
                    bottom: 22,
                    left: 0,
                    right: 0,
                    textAlign: 'center',
                    fontSize: 11,
                    letterSpacing: '0.06em',
                    color: 'var(--text-muted)',
                    textTransform: 'uppercase',
                    opacity: 0.7,
                }}
            >
                ITRIIA · Souveraineté augmentée
            </div>
        </div>
    );
}
